import React, { useState } from 'react'

import {
    Checkbox,
    ListItemIcon,
    List,
    Tooltip,
    Theme,
    makeStyles,
    ListItemText,
    Button,
    IconButton,
} from '@material-ui/core'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'

import { Container } from '../../../../../../components/Container'
import { TrainingVideoController } from '../../../../../../hooks/useTrainingVideo'
import { InitialTrainingProgress } from '../../../types'

const useStyles = makeStyles((theme: Theme) => ({
    listItem: {
        alignItems: 'center',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(1),
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    videoTitle: {
        fontWeight: 500,
    },
    playButton: {
        marginLeft: 'auto',
        marginRight: theme.spacing(2),
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover,
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
        },
        '&.Mui-disabled': {
            backgroundColor: 'transparent',
            color: theme.palette.text.disabled,
        },
    },
    checkboxContainer: {
        marginLeft: theme.spacing(1),
    },
    list: {
        width: '100%',
        padding: theme.spacing(2),
    },
}))

interface Props {
    trainingVideoController: TrainingVideoController
    theme: Theme
    setSelectedChannel: (channel: LoriChannel) => void
}

interface VideoInfo {
    id: number
    name: string
    watchedKey: keyof InitialTrainingProgress
    lockedKey: keyof InitialTrainingProgress
}

export const OnboardTraining = ({
    trainingVideoController,
    theme,
    setSelectedChannel,
}: Props) => {
    const classes = useStyles(theme)
    const [watchedVideos, setWatchedVideos] = useState<Set<number>>(new Set())

    // Get training videos from LoriController instead of hardcoding
    const videos = trainingVideoController.initialTrainingVideos
        .sort((a, b) => a.order - b.order)
        .map((item, index) => ({
            id: item.training_video.id,
            name: item.training_video.title,
            watchedKey: `video_${
                index + 1
            }_watched` as keyof InitialTrainingProgress,
            lockedKey: `video_${
                index + 1
            }_locked` as keyof InitialTrainingProgress,
        }))

    const handleWatchVideo = (videoId: number) => {
        setWatchedVideos((prev) => new Set(Array.from(prev).concat(videoId)))
        // Find the video and play it
        const trainingVideo = trainingVideoController.initialTrainingVideos.find(
            (v) => v.training_video.id === videoId,
        )
        if (trainingVideo) {
            window.open(trainingVideo.training_video.video_url, '_blank')
        }
    }

    const handleToggleComplete = (video: VideoInfo) => {
        if (!trainingVideoController.initialTrainingProgress) return

        const watchedKey = video.watchedKey
        const currentValue =
            trainingVideoController.initialTrainingProgress[watchedKey]

        // Only allow marking as complete, not incomplete
        if (currentValue) return

        trainingVideoController.updateInitialUserTrainingProgress({
            id: trainingVideoController.initialTrainingProgress.id,
            body: {
                [watchedKey]: true,
            },
        })
    }

    const isVideoLocked = (video: VideoInfo): boolean => {
        if (video.id === 1) return false
        return Boolean(
            trainingVideoController.initialTrainingProgress?.[video.lockedKey],
        )
    }

    const isVideoCompleted = (video: VideoInfo): boolean => {
        return Boolean(
            trainingVideoController.initialTrainingProgress?.[video.watchedKey],
        )
    }

    const canMarkComplete = (videoId: number): boolean => {
        const video = videos.find((v) => v.id === videoId)
        if (!video) return false

        // If video is already completed, disable checkbox
        if (isVideoCompleted(video)) return false

        return !isVideoLocked(video) && watchedVideos.has(videoId)
    }

    const getTooltipMessage = (video: VideoInfo): string => {
        if (!isVideoLocked(video)) return 'Watch video'

        const videoIndex = videos.findIndex((v) => v.id === video.id)
        if (videoIndex <= 0) return 'Watch video'

        const previousVideo = videos[videoIndex - 1]
        if (!previousVideo) return 'Watch video'

        const isVendorRequired = videoIndex === 2 // third video
        const isProjectRequired = videoIndex === 3 // fourth video

        if (isVendorRequired) {
            return trainingVideoController.initialTrainingProgress?.[
                previousVideo.watchedKey
            ]
                ? 'Property needs a vendor to unlock'
                : `Complete ${previousVideo.name} first`
        }

        if (isProjectRequired) {
            return trainingVideoController.initialTrainingProgress?.[
                previousVideo.watchedKey
            ]
                ? 'Property needs a project to unlock'
                : `Complete ${previousVideo.name} first`
        }

        return `Complete ${previousVideo.name} first`
    }

    return (
        <Container>
            <List className={classes.list}>
                {videos.map((video) => (
                    <Container key={video.id} className={classes.listItem}>
                        <ListItemText
                            primary={video.name}
                            classes={{ primary: classes.videoTitle }}
                        />

                        <Tooltip title={getTooltipMessage(video)}>
                            <span>
                                <IconButton
                                    className={classes.playButton}
                                    onClick={() => handleWatchVideo(video.id)}
                                    disabled={isVideoLocked(video)}
                                    size="small"
                                >
                                    <PlayArrowIcon />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <ListItemIcon className={classes.checkboxContainer}>
                            <Checkbox
                                checked={isVideoCompleted(video)}
                                disabled={!canMarkComplete(video.id)}
                                onChange={() => handleToggleComplete(video)}
                                disableRipple
                            />
                        </ListItemIcon>
                    </Container>
                ))}
            </List>
        </Container>
    )
}
